import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErr } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Loader2, Eye, RefreshCw, FilePlus2, CheckCircle2, Clock, Stamp, Download, WifiOff, Archive, ArchiveRestore, Printer } from "lucide-react";
import { cacheGet, cacheSet, listLocalReports } from "../lib/offline";
import { usePdfPreview } from "../components/PdfPreview";

const TYPES = [
  { k: "", label: "Tous" },
  { k: "daily", label: "Quotidiens" },
  { k: "weekly", label: "Hebdomadaires" },
  { k: "archives", label: "Archives", icon: Archive },
];
const SUP_ROLES = ["provincial_supervisor", "admin"];

export default function GeneratedReports() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [ptype, setPtype] = useState("");
  const [pending, setPending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [staleAt, setStaleAt] = useState("");
  const [locals, setLocals] = useState([]);
  const pdf = usePdfPreview();
  const archives = ptype === "archives";
  const [af, setAf] = useState({ q: "", from: "", to: "", author: "" });
  const monthOf = (r) => (r.period_key || "").slice(0, 7).replace(/-W\d+$/, "");
  const visible = !archives ? items : items.filter((r) => {
    const hay = `${r.period_label || ""} ${r.period_key || ""} ${r.scope_label || ""} ${r.author_name || ""}`.toLowerCase();
    if (af.q && !hay.includes(af.q.toLowerCase())) return false;
    if (af.author && r.author_name !== af.author) return false;
    const m = monthOf(r);
    if (af.from && m && m < af.from) return false;
    if (af.to && m && m > af.to) return false;
    return true;
  });

  const loadLocals = () => listLocalReports().then((l) => setLocals(l.map((x) => x.report)));
  useEffect(() => {
    loadLocals();
    const onChange = () => { loadLocals(); if (navigator.onLine) load(); };
    window.addEventListener("stride-queue-changed", onChange);
    return () => window.removeEventListener("stride-queue-changed", onChange);
    // eslint-disable-next-line
  }, [ptype, pending, lang]);

  const load = async (tp = ptype, pend = pending) => {
    setLoading(true);
    try {
      const params = pend ? { ptype: "weekly", status: "submitted", lang }
        : tp === "archives" ? { archived: true, lang } : (tp ? { ptype: tp, lang } : { lang });
      const res = await api.get("/harmonized/reports", { params });
      setItems(res.data);
      setStaleAt("");
      if (!pend) await cacheSet(`reports:${tp || "all"}:${lang}`, res.data);
    } catch (err) {
      if (!err.response) {
        const c = await cacheGet(`reports:${tp || "all"}:${lang}`);
        if (c?.value) { setItems(c.value); setStaleAt(c.at); }
        else toast.error(t("Hors ligne et aucun rapport encore téléchargé."));
      } else toast.error(apiErr(err.response?.data?.detail));
    }
    finally { setLoading(false); }
  };

  useEffect(() => { load(ptype, pending); /* eslint-disable-next-line */ }, [ptype, pending, lang]);

  const downloadPdf = async (r) => {
    const name = `${r.ptype === "weekly" ? "Hebdo" : "Quotidien"}_${r.period_key}.pdf`;
    const save = (blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
    };
    try {
      const res = await api.get(`/harmonized/reports/${r.id}/pdf`, { responseType: "blob", params: { lang } });
      await cacheSet(`pdf:${r.id}:${lang}`, res.data);
      save(res.data);
    } catch (err) {
      const c = await cacheGet(`pdf:${r.id}:${lang}`);
      if (c?.value) { save(c.value); toast.message(t("PDF hors ligne (dernière version téléchargée).")); }
      else if (!err.response) toast.error(t("PDF indisponible hors ligne : ouvrez-le une fois connecté."));
      else toast.error(apiErr(err.response?.data?.detail));
    }
  };

  const previewPdf = async (r) => {
    const name = `${r.ptype === "weekly" ? "Hebdo" : "Quotidien"}_${r.period_key}.pdf`;
    try {
      await pdf.show(async () => {
        try {
          const res = await api.get(`/harmonized/reports/${r.id}/pdf`, { responseType: "blob", params: { lang } });
          await cacheSet(`pdf:${r.id}:${lang}`, res.data);
          return res.data;
        } catch (err) {
          const c = await cacheGet(`pdf:${r.id}:${lang}`);
          if (c?.value) { toast.message(t("PDF hors ligne (dernière version téléchargée).")); return c.value; }
          throw err;
        }
      }, { title: `${r.period_label || r.period_key} · ${r.scope_label || ""}`, filename: name });
    } catch (err) {
      if (!err.response) toast.error(t("PDF indisponible hors ligne : ouvrez-le une fois connecté."));
      else toast.error(apiErr(err.response?.data?.detail));
    }
  };

  const toggleArchive = async (r) => {
    try {
      await api.post(`/harmonized/reports/${r.id}/archive`, { archived: !r.archived });
      toast.success(t(r.archived ? "Rapport restauré." : "Rapport archivé."));
      load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const generate = async (tp) => {    setBusy(tp);
    try {
      if (!navigator.onLine) { toast.error(t("La génération des rapports nécessite une connexion.")); return; }
      const res = await api.post("/harmonized/generate", { ptype: tp });
      toast.success(t("Brouillon généré."));
      navigate(`/generated/${res.data.id}`);
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  return (
    <div data-testid="generated-reports-page">
      <PageHeader eyebrow={t("Produits dérivés")} title={t("Rapports générés")}
        subtitle={t("Brouillons quotidiens et hebdomadaires dérivés de la saisie unique. À valider et signer avant soumission.")}
        testid="generated-header">
        <Button onClick={() => generate("daily")} disabled={!!busy} variant="outline" className="rounded-md" data-testid="regen-daily">
          {busy === "daily" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FilePlus2 className="h-4 w-4 mr-1.5" />}{t("Générer le quotidien")}
        </Button>
        <Button onClick={() => generate("weekly")} disabled={!!busy} className="rounded-md bg-[#F05A28] hover:bg-[#D84A1B]" data-testid="regen-weekly">
          {busy === "weekly" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}{t("Générer l'hebdomadaire")}
        </Button>
      </PageHeader>

      <div className="flex flex-wrap gap-2 mb-4">
        {TYPES.map((x) => (
          <button key={x.k} onClick={() => { setPending(false); setPtype(x.k); }} data-testid={`gfilter-${x.k || "all"}`}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors inline-flex items-center gap-1.5 ${!pending && ptype === x.k ? "bg-[#0F172A] text-white" : "bg-white border border-[#E2E8F0] text-slate-600 hover:bg-slate-50"}`}>
            {x.icon && <x.icon className="h-3.5 w-3.5" />}{t(x.label)}
          </button>
        ))}
        {SUP_ROLES.includes(user?.role) && (
          <button onClick={() => { setPending(true); setPtype("weekly"); }} data-testid="gfilter-pending"
            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors inline-flex items-center gap-1.5 ${pending ? "bg-[#0284C7] text-white" : "bg-white border border-[#0284C7]/40 text-[#0284C7] hover:bg-[#E0F2FE]"}`}>
            <Stamp className="h-3.5 w-3.5" />{t("À valider")}
          </button>
        )}
      </div>

      {pending && (
        <div className="mb-4 rounded-lg border border-[#0284C7]/30 bg-[#E0F2FE] px-4 py-3 text-sm text-[#075985]" data-testid="pending-banner">
          {items.length === 0
            ? t("Aucun rapport hebdomadaire n'attend votre validation.")
            : `${items.length} ${t("rapport(s) hebdomadaire(s) soumis par leur auteur et en attente de votre validation et signature.")}`}
        </div>
      )}

      {archives && (
        <div className="mb-4 rounded-lg border border-slate-300 bg-slate-100 px-4 py-3 text-sm text-slate-700" data-testid="archives-banner">
          {t("Archives : rapports archivés manuellement ou automatiquement après 3 mois. Ils restent consultables et imprimables.")}
        </div>
      )}
      {archives && (
        <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2" data-testid="archive-filters">
          <Input placeholder={t("Rechercher (période, zone, auteur)…")} value={af.q} onChange={(e) => setAf({ ...af, q: e.target.value })} className="h-9 lg:col-span-2" data-testid="archive-search" />
          <Input type="month" value={af.from} onChange={(e) => setAf({ ...af, from: e.target.value })} className="h-9" title={t("Du (mois)")} data-testid="archive-from" />
          <Input type="month" value={af.to} onChange={(e) => setAf({ ...af, to: e.target.value })} className="h-9" title={t("Au (mois)")} data-testid="archive-to" />
          <select value={af.author} onChange={(e) => setAf({ ...af, author: e.target.value })} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="archive-author">
            <option value="">{t("Tous les auteurs")}</option>
            {[...new Set(items.map((r) => r.author_name).filter(Boolean))].sort().map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
      )}

      {staleAt && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-slate-300 bg-slate-100 px-4 py-2.5 text-xs text-slate-700" data-testid="offline-stale-banner">
          <WifiOff className="h-3.5 w-3.5" />
          {t("Données hors ligne · dernier rafraîchissement le")} {new Date(staleAt).toLocaleString()}
        </div>
      )}

      <div className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm overflow-hidden">
        {locals.filter((r) => !pending && !archives && (!ptype || r.ptype === ptype)).map((r) => (
          <div key={r.id} className="flex flex-wrap items-center gap-3 border-b border-amber-200 bg-amber-50 px-4 py-3 text-sm" data-testid={`generated-local-${r.period_key}`}>
            <WifiOff className="h-4 w-4 text-amber-700" />
            <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded ${r.ptype === "weekly" ? "bg-[#E0F2FE] text-[#0284C7]" : "bg-[#FFF3EE] text-[#F05A28]"}`}>
              {t(r.ptype === "weekly" ? "Hebdomadaire" : "Quotidien")}
            </span>
            <span className="font-medium text-slate-800">{r.period_label}</span>
            <span className="text-xs text-amber-800">{t("Généré hors ligne · envoyé au serveur à la reconnexion")}</span>
            <Button size="sm" variant="ghost" className="ml-auto" onClick={() => navigate(`/generated/${r.id}`)} data-testid={`generated-local-view-${r.period_key}`}>
              <Eye className="h-4 w-4" />
            </Button>
          </div>
        ))}
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#F05A28]" /></div>
        ) : visible.length === 0 ? (
          <p className="p-8 text-center text-sm text-slate-400" data-testid="generated-empty">{t(archives ? (items.length ? "Aucun rapport archivé ne correspond aux filtres." : "Aucun rapport archivé.") : "Aucun brouillon généré pour le moment.")}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-[#F1F5F9] text-left">
                {["Type", "Période", "Portée", "Auteur", "Saisies", "Statut", ""].map((h, i) => (
                  <th key={i} className="p-3 text-xs font-bold text-slate-600">{t(h)}</th>))}
              </tr></thead>
              <tbody>
                {visible.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100 hover:bg-[#F8FAFC]" data-testid={`generated-row-${r.id}`}>
                    <td className="p-3">
                      <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded ${r.ptype === "weekly" ? "bg-[#E0F2FE] text-[#0284C7]" : "bg-[#FFF3EE] text-[#F05A28]"}`}>
                        {t(r.ptype === "weekly" ? "Hebdomadaire" : "Quotidien")}
                      </span>
                      {r.auto && <span className="ml-1.5 text-[10px] text-slate-400">{t("auto")}</span>}
                      {r.archived && <span className="ml-1.5 text-[10px] text-slate-500 inline-flex items-center gap-0.5"><Archive className="h-3 w-3" />{t(r.auto_archived ? "archivé auto" : "archivé")}</span>}
                    </td>
                    <td className="p-3 font-medium text-slate-800">{r.period_label || r.period_key}</td>
                    <td className="p-3 text-slate-600">{r.scope_label}</td>
                    <td className="p-3 text-slate-600">{r.author_name || "—"}</td>
                    <td className="p-3 text-slate-600">{r.entries_count}</td>
                    <td className="p-3">
                      {r.status === "consolidated" ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#0284C7]"><Stamp className="h-3.5 w-3.5" />{t("Consolidé")}</span>
                      ) : r.status === "validated" ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" />{t("Validé")}</span>
                      ) : r.status === "submitted" ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700"><Clock className="h-3.5 w-3.5" />{t("Soumis")}</span>
                      ) : r.status === "returned" ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700"><Clock className="h-3.5 w-3.5" />{t("À corriger")}</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500"><Clock className="h-3.5 w-3.5" />{t("Brouillon")}</span>
                      )}
                    </td>
                    <td className="p-3 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => previewPdf(r)} data-testid={`generated-preview-${r.id}`} title={t("Aperçu avant impression")}>
                        <Printer className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => downloadPdf(r)} data-testid={`generated-pdf-${r.id}`} title={t("Télécharger PDF")}>
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => toggleArchive(r)} data-testid={`generated-archive-${r.id}`} title={t(r.archived ? "Restaurer" : "Archiver")}>
                        {r.archived ? <ArchiveRestore className="h-4 w-4 text-emerald-700" /> : <Archive className="h-4 w-4 text-slate-500" />}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => navigate(`/generated/${r.id}`)} data-testid={`generated-view-${r.id}`}>
                        <Eye className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {pdf.el}
    </div>
  );
}
